import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  Camera,
  Check,
  Loader2,
  Plus,
  Upload,
  X,
} from "lucide-react";
import {
  applyProfileLocation,
  getUserProfile,
  profileCreation,
  resetProfileURL,
  uploadProfileImage,
} from "reducer/profileSlice";
import { businessProfileCreation } from "reducer/usersSlice";
import {
  compressImage,
  serverFiles,
  formatPersonName,
  splitPersonName,
  KNOWN_INDUSTRIES,
  splitProfileIndustries,
} from "utils/utils";
import toast from "components/Toast/toast";

const INDUSTRIES = KNOWN_INDUSTRIES;

const field =
  "w-full rounded-xl bg-zinc-900/70 border border-zinc-800 text-zinc-100 placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:border-[#6C63F5] transition-colors";

const ChipButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
      active
        ? "border-[#6C63F5] bg-[#6C63F5]/15 text-zinc-50"
        : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-600"
    }`}
  >
    {active && <Check size={12} className="text-[#6C63F5]" />} {children}
  </button>
);

const categoryIdsFromProfile = (profile) => {
  if (Array.isArray(profile?.categoryId) && profile.categoryId.length) {
    return profile.categoryId;
  }
  if (Array.isArray(profile?.categories) && profile.categories.length) {
    return profile.categories.map((c) => c.id).filter(Boolean);
  }
  const companyCats = profile?.companyResponseDto?.categories;
  if (Array.isArray(companyCats) && companyCats.length) {
    return companyCats.map((c) => c.id).filter(Boolean);
  }
  return [];
};

const pickLocationText = (profile) => ({
  city:
    profile?.cityName ||
    profile?.city?.name ||
    profile?.companyResponseDto?.cityName ||
    profile?.companyResponseDto?.city?.name ||
    profile?.companyResponseDto?.locationCity ||
    "",
  country:
    profile?.companyResponseDto?.locationCountry ||
    profile?.stateName ||
    profile?.state?.name ||
    profile?.companyResponseDto?.stateName ||
    profile?.companyResponseDto?.state?.name ||
    "",
});

const EditProfileModal = ({ open, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { userProfile } = useSelector((state) => state.userProfile);
  const isBrand = userProfile?.userType === "BUSINESS";

  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");
  const [industries, setIndustries] = useState([]);
  const [otherIndustry, setOtherIndustry] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleDraft, setRoleDraft] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const avatarRef = useRef(null);
  const hydratedOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      hydratedOpen.current = false;
      setOtherIndustry("");
      return;
    }
    if (!userProfile || hydratedOpen.current) return;
    hydratedOpen.current = true;

    const knownSet = new Set(INDUSTRIES.map((i) => i.toLowerCase()));
    if (isBrand) {
      setName(userProfile?.companyResponseDto?.name || "");
      setWebsite(userProfile?.companyResponseDto?.websiteUrl || "");
      const raw = (
        userProfile?.companyResponseDto?.industry ||
        userProfile?.industry ||
        []
      ).filter(Boolean);
      const known = raw.filter((i) => knownSet.has(String(i).toLowerCase()));
      const custom = raw.find((i) => !knownSet.has(String(i).toLowerCase()));
      setIndustries(
        (custom ? [...known, "Other"] : known).slice(0, 3)
      );
      setOtherIndustry(custom || "");
      setRoles([]);
    } else {
      setName(
        formatPersonName(userProfile?.firstName, userProfile?.lastName)
      );
      setWebsite("");
      const rawIndustry = Array.isArray(userProfile?.industry)
        ? userProfile.industry.filter(Boolean)
        : [];
      const rawSkills = Array.isArray(userProfile?.skills)
        ? userProfile.skills.filter(Boolean)
        : Array.isArray(userProfile?.roles)
          ? userProfile.roles.filter(Boolean)
          : [];
      const { industries: splitInd, roles: splitRoles } =
        splitProfileIndustries(rawIndustry, rawSkills, { isBrand: false });
      const known = splitInd.filter((i) =>
        knownSet.has(String(i).toLowerCase())
      );
      const custom = splitInd.find(
        (i) => !knownSet.has(String(i).toLowerCase())
      );
      setIndustries((custom ? [...known, "Other"] : known).slice(0, 3));
      setOtherIndustry(custom || "");
      setRoles(splitRoles.slice(0, 2));
    }
    setBio(userProfile?.bio || userProfile?.about || "");
    const loc = pickLocationText(userProfile);
    setCity(loc.city);
    setCountry(loc.country);
    const img =
      userProfile?.companyResponseDto?.logo || userProfile?.profileUrl;
    setAvatarPreview(img ? serverFiles(img) : "");
    setAvatarFile(null);
    setRoleDraft("");
    setErr("");
  }, [open, userProfile, isBrand]);

  const toggleIndustry = (i) => {
    setIndustries((cur) => {
      if (cur.includes(i)) {
        if (i === "Other") setOtherIndustry("");
        return cur.filter((x) => x !== i);
      }
      if (cur.length >= 3) return cur;
      return [...cur, i];
    });
  };

  const finalIndustries = () =>
    industries
      .map((i) => (i === "Other" ? otherIndustry.trim() : i))
      .filter(Boolean);

  const addRole = () => {
    const v = roleDraft.trim();
    if (!v || roles.includes(v) || roles.length >= 2) {
      setRoleDraft("");
      return;
    }
    setRoles((r) => [...r, v]);
    setRoleDraft("");
  };

  const roleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addRole();
    } else if (e.key === "Backspace" && !roleDraft && roles.length) {
      setRoles((r) => r.slice(0, -1));
    }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    setAvatarBusy(true);
    try {
      const compressed = await compressImage(file);
      setAvatarFile(compressed);
      setAvatarPreview(URL.createObjectURL(compressed));
    } catch (_) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemoveAvatar = async () => {
    const hadSavedPhoto = !!(
      userProfile?.profileUrl ||
      userProfile?.companyResponseDto?.logo
    );
    setAvatarPreview("");
    setAvatarFile(null);
    if (avatarRef.current) avatarRef.current.value = "";

    if (!hadSavedPhoto) return;

    setAvatarBusy(true);
    try {
      dispatch(resetProfileURL());
      await dispatch(
        uploadProfileImage({
          payload: null,
          type: isBrand ? "BUSINESS" : "CREATOR",
        })
      ).unwrap();
      await dispatch(getUserProfile());
    } catch (_) {
      setErr("Could not remove photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr(isBrand ? "Brand name is required" : "Name is required");
      return;
    }

    let named = null;
    let nextRoles = roles;
    if (!isBrand) {
      named = splitPersonName(name);
      if (named.error) {
        setErr(named.error);
        return;
      }
      nextRoles = [...roles];
      const draft = roleDraft.trim();
      if (
        draft &&
        nextRoles.length < 2 &&
        !nextRoles.some((r) => r.toLowerCase() === draft.toLowerCase())
      ) {
        nextRoles.push(draft);
      }
      if (!nextRoles.length) {
        setErr("Add at least one type of creative");
        return;
      }
    }

    if (industries.includes("Other") && !otherIndustry.trim()) {
      setErr("Please tell us your industry (Other).");
      return;
    }

    const industryList = finalIndustries();

    setBusy(true);
    setErr("");
    try {
      const categoryId = categoryIdsFromProfile(userProfile);
      const locationCity = city.trim();
      const locationCountry = country.trim();

      if (isBrand) {
        await dispatch(
          businessProfileCreation({
            userType: "BUSINESS",
            name: name.trim(),
            supportContactName: name.trim(),
            establishmentYear:
              userProfile?.companyResponseDto?.establishmentYear ||
              new Date().getFullYear(),
            categoryId,
            industry: industryList.length
              ? industryList
              : userProfile?.companyResponseDto?.industry || [],
            websiteUrl: website || undefined,
            about: bio || undefined,
            stateId: null,
            cityId: null,
            locationCity,
            locationCountry,
          })
        ).unwrap();
      } else {
        await dispatch(
          profileCreation({
            firstName: named.firstName,
            lastName: named.lastName,
            userType: "CREATOR",
            industry: industryList,
            categoryId,
            ...(userProfile?.gender ? { gender: userProfile.gender } : {}),
            stateId: null,
            cityId: null,
            locationCity,
            locationCountry,
            bio: bio || undefined,
            skills: nextRoles,
            ...(userProfile?.mobileNo ? { mobileNo: userProfile.mobileNo } : {}),
          })
        ).unwrap();
      }

      if (avatarFile) {
        const formData = new FormData();
        formData.append("file", avatarFile);
        await dispatch(
          uploadProfileImage({
            payload: formData,
            type: isBrand ? "BUSINESS" : "CREATOR",
          })
        );
      }

      await dispatch(getUserProfile());
      toast.success("Profile updated");
      if (onSaved) await Promise.resolve(onSaved());
      // Show the free-text location just saved (source of truth for display)
      dispatch(
        applyProfileLocation({
          cityName: locationCity || null,
          stateName: locationCountry || null,
          cityId: null,
          stateId: null,
        })
      );
      onClose();
    } catch (er) {
      setErr(
        typeof er === "string" ? er : er?.message || "Could not save profile"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[75] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-xl rounded-2xl border border-zinc-800 bg-zinc-950 p-6 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#8b83ff] font-semibold">
                  Your profile
                </div>
                <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-zinc-50">
                  Edit profile
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-zinc-500 hover:text-zinc-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-5">
              <label className="block text-sm text-zinc-300 mb-2 font-medium">
                {isBrand ? "Logo" : "Profile photo"}
              </label>
              <input
                ref={avatarRef}
                type="file"
                accept="image/*"
                hidden
                onChange={onPickAvatar}
              />
              <div className="flex items-center gap-4">
                <div
                  className={`h-20 w-20 shrink-0 overflow-hidden ${
                    isBrand ? "rounded-2xl" : "rounded-full"
                  } border-2 border-dashed border-zinc-700 flex items-center justify-center bg-zinc-900/60`}
                >
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt=""
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <Camera size={22} className="text-zinc-600" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => avatarRef.current?.click()}
                    disabled={avatarBusy}
                    className="inline-flex items-center gap-2 rounded-full border border-zinc-700 hover:border-[#6C63F5]/60 bg-zinc-900/60 px-4 py-2 text-sm text-zinc-100 transition-colors disabled:opacity-50"
                  >
                    {avatarBusy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Upload size={14} className="text-[#8b83ff]" />
                    )}
                    {avatarPreview ? "Replace" : "Upload"}
                  </button>
                  {avatarPreview && (
                    <button
                      type="button"
                      onClick={onRemoveAvatar}
                      disabled={avatarBusy}
                      className="text-xs text-zinc-500 hover:text-red-300 transition-colors disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm text-zinc-300 mb-2 font-medium">
                  {isBrand ? "Brand name" : "Full name"}
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isBrand ? "Studio Nord" : "Your name"}
                  className={field}
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-300 mb-2 font-medium">
                  Bio
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={3}
                  placeholder="A short byline about you or your brand"
                  className={`${field} resize-none`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-zinc-300 mb-2 font-medium">
                    City{" "}
                    <span className="text-zinc-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Mumbai"
                    className={field}
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-300 mb-2 font-medium">
                    Country{" "}
                    <span className="text-zinc-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="India"
                    className={field}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-300 mb-2 font-medium">
                  Industries{" "}
                  <span className="text-zinc-500 font-normal">
                    (pick up to 3)
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {[...INDUSTRIES, "Other"].map((i) => (
                    <ChipButton
                      key={i}
                      active={industries.includes(i)}
                      onClick={() => toggleIndustry(i)}
                    >
                      {i}
                    </ChipButton>
                  ))}
                </div>
                {industries.includes("Other") && (
                  <input
                    type="text"
                    value={otherIndustry}
                    onChange={(e) => setOtherIndustry(e.target.value)}
                    placeholder="Which industry?"
                    className={`${field} mt-3`}
                  />
                )}
              </div>

              {!isBrand && (
                <div>
                  <label className="block text-sm text-zinc-300 mb-2 font-medium">
                    Type of creative{" "}
                    <span className="text-zinc-500 font-normal">
                      (press Enter to add — up to 2)
                    </span>
                  </label>
                  {roles.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {roles.map((r) => (
                        <span
                          key={r}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#6C63F5]/40 bg-[#6C63F5]/15 text-[#c9c5ff] px-3 py-1.5 text-xs font-medium"
                        >
                          {r}
                          <button
                            type="button"
                            onClick={() =>
                              setRoles((cur) => cur.filter((x) => x !== r))
                            }
                            className="text-[#8b83ff] hover:text-white"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      value={roleDraft}
                      onChange={(e) => setRoleDraft(e.target.value)}
                      onKeyDown={roleKeyDown}
                      disabled={roles.length >= 2}
                      placeholder={
                        roles.length >= 2
                          ? "You've added 2 roles — remove one to change"
                          : "e.g. Photographer, Stylist, Cinematographer"
                      }
                      className={`${field} pr-24`}
                    />
                    {roles.length < 2 && roleDraft.trim() && (
                      <button
                        type="button"
                        onClick={addRole}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-[#6C63F5] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#5751E5]"
                      >
                        <Plus size={12} /> Add
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isBrand && (
                <div>
                  <label className="block text-sm text-zinc-300 mb-2 font-medium">
                    Website
                  </label>
                  <input
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="studionord.com"
                    className={field}
                  />
                </div>
              )}
            </div>

            {err && (
              <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                {err}
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-zinc-700 text-zinc-200 hover:border-zinc-500 px-5 py-2.5 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="rounded-full bg-[#6C63F5] text-white hover:bg-[#5751E5] px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 w-auto shrink-0"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}{" "}
                Save changes
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditProfileModal;
